import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, useToast } from '../../components/ui';
import { listOutreachRuns, getOutreachRun, getOutreachSettings, stopOutreachRun, deleteOutreachRun } from '../../utils/api';
import RunResearchModal from '../components/RunResearchModal';
import ConfirmDialog from '../../marketing/components/ConfirmDialog';
import { ACTIVE_RUN_STATUSES, MODE_LABELS, RUN_STATUS_LABELS, fmtCost, fmtDateTime, fmtDuration, fmtNumber, loadError, runProgress } from '../utils';

const POLL_MS = 8000;
const SOURCE_LABELS = { nppes: 'CMS NPPES registry', hrsa: 'HRSA health centers', mdhhs: 'Medicaid plan directories', census: 'US Census ACS', website: 'Practice websites' };

function ProgressBar({ run, style }) {
  const p = runProgress(run);
  return (
    <div style={style}>
      <div className="ops-progress" role="progressbar" aria-valuenow={p.percent} aria-valuemin={0} aria-valuemax={100} aria-label="Run progress" style={{ margin: '0 0 6px', maxWidth: 'none' }}><div className="ops-progress__bar" style={{ width: `${p.percent}%` }} /></div>
      <div className="ops-help">{p.label} ({p.percent}%)</div>
    </div>
  );
}

function DeltaList({ title, rows, render }) {
  if (!rows?.length) return null;
  return (
    <div className="ops-form-section" style={{ marginTop: 14 }}>
      <h3>{title} ({rows.length})</h3>
      <ul className="ops-list" style={{ border: '1px solid var(--line)', borderRadius: 8 }}>
        {rows.slice(0, 50).map((r, i) => (
          <li key={r.officeId || i}>
            <div className="ops-list__main"><strong><Link className="ops-link" to={`/outreach/offices?office=${encodeURIComponent(r.officeId)}`}>{r.name}</Link>{r.areaLabel ? ` · ${r.areaLabel}` : ''}{r.priority ? ` · ${r.priority}` : ''}</strong><span style={{ whiteSpace: 'normal' }}>{render(r)}</span></div>
          </li>
        ))}
        {rows.length > 50 && <li><span className="ops-help">{rows.length - 50} more not shown</span></li>}
      </ul>
    </div>
  );
}

export default function RunsPage() {
  const [params, setParams] = useSearchParams();
  const [showToast, toastNode] = useToast();
  const selected = params.get('run');
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [settings, setSettings] = useState(null);
  const [showRun, setShowRun] = useState(false);
  const [tick, setTick] = useState(0);
  // { action: 'stop' | 'delete', run } while a confirmation is open; busy while the request runs.
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => listOutreachRuns({ limit: 25 }).then(r => { setPage(r); setError(''); }).catch(e => { setError(loadError(e, 'Could not load the run log')); setPage({ items: [], active: null }); }), []);
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { getOutreachSettings().then(r => setSettings(r.settings)).catch(() => {}); }, []);

  const loadDetail = useCallback(() => {
    if (!selected) { setDetail(null); return; }
    getOutreachRun(selected).then(d => { setDetail(d); setDetailError(''); }).catch(e => setDetailError(loadError(e, 'Could not load this run')));
  }, [selected]);
  useEffect(() => { setDetail(null); loadDetail(); }, [loadDetail]);

  const activeList = Boolean(page?.active);
  const activeDetail = detail?.run && ACTIVE_RUN_STATUSES.includes(detail.run.status);
  useEffect(() => {
    if (!activeList && !activeDetail) return undefined;
    const id = setInterval(() => { load(); if (selected) loadDetail(); }, POLL_MS);
    return () => clearInterval(id);
  }, [activeList, activeDetail, load, loadDetail, selected]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && selected) setParams({}, { replace: true }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, setParams]);

  const open = (id) => setParams({ run: id }, { replace: true });
  const close = () => setParams({}, { replace: true });
  const run = detail?.run;
  const delta = detail?.delta;
  const isActive = (r) => Boolean(r) && ACTIVE_RUN_STATUSES.includes(r.status);

  const confirmPending = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.action === 'stop') {
        await stopOutreachRun(pending.run.runId);
        showToast('Run stopped. Finished analyses were kept.');
      } else {
        await deleteOutreachRun(pending.run.runId);
        showToast('Run deleted');
        if (selected === pending.run.runId) close();
      }
      setPending(null);
      setTick(t => t + 1);
      if (selected && selected !== pending.run.runId) loadDetail();
      else if (selected && pending.action === 'stop') loadDetail();
    } catch (e) {
      showToast(loadError(e, pending.action === 'stop' ? 'Could not stop this run' : 'Could not delete this run'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Agent runs"
        subtitle="Each research run collects public source data, compares it with what is already saved and sends only new or materially changed offices to the AI model."
        actions={<button type="button" className="ops-btn ops-btn--primary ops-btn--sm" onClick={() => setShowRun(true)} disabled={activeList}><Icon name="play" size={14} /> {activeList ? 'Run in progress' : 'Run research'}</button>}
      />
      {error && <div className="ops-error">{error}</div>}
      {activeList && (
        <div className="ops-info" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>A run is in progress. This page refreshes automatically.</span>
            <button type="button" className="ops-btn ops-btn--sm" onClick={() => setPending({ action: 'stop', run: page.active })}><Icon name="pause" size={14} /> Stop run</button>
          </div>
          <ProgressBar run={page.active} style={{ marginTop: 8 }} />
        </div>
      )}

      <Panel flush>
        {!page ? <div style={{ padding: 18 }}><Skeleton rows={6} /></div> : !page.items.length ? <Empty title="No research runs yet" /> : (
          <div className="ops-table-wrap">
            <table className="ops-table">
              <thead><tr><th>Started</th><th>Mode</th><th>Trigger</th><th>Status</th><th className="num">Candidates</th><th className="num">New</th><th className="num">Updated</th><th className="num">AI calls</th><th className="num">Cache hits</th><th className="num">Est. cost</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {page.items.map(r => (
                  <tr key={r.runId} className={`is-clickable${selected === r.runId ? ' is-selected' : ''}`} onClick={() => open(r.runId)}>
                    <td><div className="ops-cell-main">{fmtDateTime(r.startedAt || r.createdAt)}</div>{r.durationMs != null && <div className="ops-cell-sub">{fmtDuration(r.durationMs)}</div>}</td>
                    <td>{MODE_LABELS[r.mode] || r.mode}</td>
                    <td>{r.trigger === 'schedule' ? 'Scheduled' : `Manual${r.startedBy ? ` · ${r.startedBy}` : ''}`}</td>
                    <td><StatusPill status={r.status} label={RUN_STATUS_LABELS[r.status] || r.status} /></td>
                    <td className="num">{r.candidateCount ?? ''}</td>
                    <td className="num">{r.delta?.created ?? ''}</td>
                    <td className="num">{r.delta?.updated ?? ''}</td>
                    <td className="num">{r.aiStats?.modelCalls ?? ''}</td>
                    <td className="num">{r.aiStats?.cacheHits ?? ''}</td>
                    <td className="num">{r.aiUsage?.estimatedCostUsd != null ? fmtCost(r.aiUsage.estimatedCostUsd) : <span className="muted">n/a</span>}</td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      {isActive(r)
                        ? <button type="button" className="ops-btn ops-btn--ghost ops-btn--sm" onClick={e => { e.stopPropagation(); setPending({ action: 'stop', run: r }); }} aria-label="Stop this run"><Icon name="pause" size={14} /> Stop</button>
                        : <button type="button" className="ops-btn ops-btn--ghost ops-btn--sm" onClick={e => { e.stopPropagation(); setPending({ action: 'delete', run: r }); }} aria-label="Delete this run"><Icon name="trash" size={14} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {selected && (
        <div className="ops-drawer__backdrop" onClick={close}>
          <aside className="ops-drawer" role="dialog" aria-modal="true" aria-label="Run detail" onClick={e => e.stopPropagation()}>
            <div className="ops-drawer__head">
              <div><div className="ops-cell-main" style={{ fontSize: 16 }}>{run ? MODE_LABELS[run.mode] || run.mode : 'Run'}</div><div className="ops-cell-sub">{run ? `${fmtDateTime(run.startedAt || run.createdAt)} · ${run.trigger === 'schedule' ? 'Scheduled' : `Manual by ${run.startedBy || 'staff'}`}` : selected}</div></div>
              <button type="button" className="ops-close" onClick={close} aria-label="Close"><Icon name="x" size={16} /></button>
            </div>
            <div className="ops-drawer__body">
              {detailError && <div className="ops-error">{detailError}</div>}
              {!run && !detailError && <Skeleton rows={8} />}
              {run && (
                <>
                  <div className="ops-actions" style={{ marginBottom: 12, gap: 6, flexWrap: 'wrap' }}>
                    <StatusPill status={run.status} label={RUN_STATUS_LABELS[run.status] || run.status} />
                    {run.aiRequired === false && <StatusPill status="completed" label="No AI calls needed" />}
                    {run.notifyByEmail && <StatusPill status={run.notification?.error ? 'failed' : run.notification?.sent ? 'completed' : 'neutral'} label={run.notification?.error ? 'Email failed' : run.notification?.sent ? 'Email sent' : 'Email requested'} />}
                  </div>
                  {isActive(run) && <ProgressBar run={run} style={{ marginBottom: 12 }} />}
                  {run.status === 'stopped' && <div className="ops-info" style={{ marginBottom: 12 }}>Stopped{run.stoppedBy ? ` by ${run.stoppedBy}` : ''}{run.stoppedAt ? ` at ${fmtDateTime(run.stoppedAt)}` : ''}. Analyses that finished before the stop were saved and will be reused by the next run.</div>}
                  {run.aiStats?.message && <div className="ops-info" style={{ marginBottom: 12 }}>{run.aiStats.message}</div>}
                  {run.summary?.headline && <div className="ops-note" style={{ marginBottom: 12 }}><strong>{run.summary.headline}</strong>{run.summary.generatedBy ? <div className="ops-help">Summary {run.summary.generatedBy === 'deterministic' ? 'generated without AI' : run.summary.generatedBy === 'ai_cached' ? 'reused from cache' : 'written by the AI model'}.</div> : null}</div>}

                  <dl className="ops-dl">
                    <div><dt>Stage</dt><dd>{isActive(run) ? runProgress(run).label : run.status === 'stopped' ? `Stopped during ${runProgress(run).label.toLowerCase()}` : 'Done'}</dd></div>
                    <div><dt>Finished</dt><dd>{run.finishedAt ? fmtDateTime(run.finishedAt) : <span className="muted">Not yet</span>}{run.durationMs != null && <div className="ops-cell-sub">{fmtDuration(run.durationMs)}</div>}</dd></div>
                    <div><dt>Candidates collected</dt><dd>{run.candidateCount ?? 0}{run.existingOfficeCount != null ? <div className="ops-cell-sub">{run.existingOfficeCount} already saved</div> : null}</dd></div>
                    <div><dt>Sent to AI</dt><dd>{run.aiCandidateCount ?? 0}<div className="ops-cell-sub">{run.aiStats?.modelCalls ?? 0} model calls · {run.aiStats?.cacheHits ?? 0} cache hits{run.aiStats?.errors?.length ? ` · ${run.aiStats.errors.length} errors` : ''}</div></dd></div>
                    <div><dt>Tokens</dt><dd>{run.aiUsage ? `${fmtNumber(run.aiUsage.inputTokens || 0)} in / ${fmtNumber(run.aiUsage.outputTokens || 0)} out` : <span className="muted">None</span>}</dd></div>
                    <div><dt>Estimated cost</dt><dd>{run.aiUsage?.estimatedCostUsd != null ? fmtCost(run.aiUsage.estimatedCostUsd) : <span className="muted">Enter token prices in Settings to estimate</span>}{run.aiUsage?.stoppedReason && <div className="ops-cell-sub neg">Stopped early: {run.aiUsage.stoppedReason}</div>}</dd></div>
                  </dl>

                  {run.changeCounts && (
                    <div className="ops-form-section" style={{ marginTop: 14 }}>
                      <h3>Change detection</h3>
                      <div className="ops-stat-row">
                        {Object.entries(run.changeCounts).map(([k, v]) => <div key={k} className="ops-stat"><b>{v}</b><span>{k.toLowerCase().replace(/_/g, ' ')}</span></div>)}
                      </div>
                    </div>
                  )}

                  {!!run.sourceReports?.length && (
                    <div className="ops-form-section" style={{ marginTop: 14 }}>
                      <h3>Public sources</h3>
                      <table className="ops-table ops-table--dense">
                        <thead><tr><th>Source</th><th>Status</th><th className="num">Records</th><th>Notes</th></tr></thead>
                        <tbody>
                          {run.sourceReports.map(s => (
                            <tr key={s.source}>
                              <td>{SOURCE_LABELS[s.source] || s.source}</td>
                              <td><StatusPill status={s.status === 'ok' ? 'completed' : s.status === 'partial' ? 'completed_with_errors' : s.status === 'skipped' ? 'skipped' : 'failed'} label={s.status} /></td>
                              <td className="num">{s.candidates ?? 0}</td>
                              <td className="ops-cell-sub">{(s.errors || []).slice(0, 3).join('; ')}{s.durationMs != null ? ` ${fmtDuration(s.durationMs)}` : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {!!run.summary?.highlights?.length && <div className="ops-form-section" style={{ marginTop: 14 }}><h3>Highlights</h3><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{run.summary.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul></div>}
                  {!!run.summary?.recommendedActions?.length && <div className="ops-form-section" style={{ marginTop: 14 }}><h3>Recommended next steps</h3><ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{run.summary.recommendedActions.map((h, i) => <li key={i}>{h}</li>)}</ul></div>}

                  <DeltaList title="New offices" rows={delta?.created} render={r => (r.changedFields?.length ? r.changedFields.join(', ') : 'Added from public sources')} />
                  <DeltaList title="Updated offices" rows={delta?.updated} render={r => (r.changedFields?.length ? `Changed: ${r.changedFields.join(', ')}` : 'Updated')} />
                  <DeltaList title="Closure signals" rows={delta?.closed} render={() => 'No longer listed in the public sources. Confirm before removing.'} />
                  <DeltaList title="Possible duplicates" rows={delta?.duplicates} render={r => `May match ${r.matchesName || r.matchesOfficeId}${r.reasons?.length ? ` (${r.reasons.join(', ')})` : ''}`} />
                  <DeltaList title="Needs verification" rows={delta?.verification} render={r => (r.reasons || []).join('; ')} />

                  {!!run.errors?.length && (
                    <div className="ops-form-section" style={{ marginTop: 14 }}>
                      <h3>Errors</h3>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{run.errors.map((e, i) => <li key={i} className="neg">{typeof e === 'string' ? e : e.message || JSON.stringify(e)}</li>)}</ul>
                    </div>
                  )}
                  {run.notification?.error && <div className="ops-error" style={{ marginTop: 12 }}>Email report failed: {run.notification.error}</div>}
                  {run.failure && <div className="ops-error" style={{ marginTop: 12 }}>{typeof run.failure === 'string' ? run.failure : `Run failed at ${run.failure.stage || 'unknown stage'}: ${run.failure.message || run.failure.error || JSON.stringify(run.failure)}`}</div>}
                  {run.lastError && !run.failure && <div className="ops-error" style={{ marginTop: 12 }}>{typeof run.lastError === 'string' ? run.lastError : run.lastError.message}</div>}
                  <div className="ops-actions" style={{ marginTop: 16, gap: 8, flexWrap: 'wrap' }}>
                    {isActive(run)
                      ? <button type="button" className="ops-btn ops-btn--sm" onClick={() => setPending({ action: 'stop', run })}><Icon name="pause" size={14} /> Stop run</button>
                      : <button type="button" className="ops-btn ops-btn--danger ops-btn--sm" onClick={() => setPending({ action: 'delete', run })}><Icon name="trash" size={14} /> Delete run</button>}
                  </div>
                  <div className="ops-help" style={{ marginTop: 14 }}>Run {run.runId}. Model {settings?.ai?.modelId || 'per settings'}.</div>
                </>
              )}
            </div>
          </aside>
        </div>
      )}

      {showRun && (
        <RunResearchModal
          onClose={() => setShowRun(false)}
          emailDefault={settings?.notifications?.emailOnManualRun}
          costCapUsd={settings?.ai?.maxCostPerRunUsd}
          onStarted={(r) => { setShowRun(false); showToast('Research run started'); setTick(t => t + 1); open(r.runId); }}
        />
      )}
      {pending?.action === 'stop' && (
        <ConfirmDialog title="Stop this run?" confirmLabel="Yes, stop the run" busy={busy} onConfirm={confirmPending} onCancel={() => { if (!busy) setPending(null); }}>
          The run ends at its current step. Offices already analyzed are kept and reused by the next run, so no AI spend is wasted. Offices not yet analyzed stay as they were.
        </ConfirmDialog>
      )}
      {pending?.action === 'delete' && (
        <ConfirmDialog title="Delete this run?" confirmLabel="Yes, delete" busy={busy} onConfirm={confirmPending} onCancel={() => { if (!busy) setPending(null); }}>
          This removes the run from the log along with its stored source data and analysis files. Offices, staff notes and the AI cache are not affected. This cannot be undone.
        </ConfirmDialog>
      )}
      {toastNode}
    </>
  );
}
